import type {
  SmartCreationAutomaticBidInitialization,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";
import { SmartCreationReviewBidExpressionSchema } from "@adpilot/shared";
import { useQueries } from "@tanstack/react-query";
import { fetchSmartCreationReviewBidPreview } from "@/lib/smart-creation-api";
import { bidExpression, bidExpressionKey, type Entry } from "./target-entries";

interface ReviewBidPreviewOptions {
  workspace: SmartCreationReviewWorkspace;
  planId: string | undefined;
  planShopId: string | undefined;
  activeGroup: string;
  positiveEntries: Entry[];
  automaticInitialization: SmartCreationAutomaticBidInitialization | undefined;
  reviewStatus: SmartCreationReviewWorkspace["status"] | undefined;
  stale: boolean;
  negativeMode: boolean;
}

/** Amazon bid recommendations for the active ad group's selected target expressions. */
export function useReviewBidPreview({
  workspace,
  planId,
  planShopId,
  activeGroup,
  positiveEntries,
  automaticInitialization,
  reviewStatus,
  stale,
  negativeMode,
}: ReviewBidPreviewOptions) {
  const bidExpressionsJson = JSON.stringify([
    ...new Map(
      positiveEntries.flatMap((entry) => {
        const expression = bidExpression(entry);
        return expression ? [[bidExpressionKey(expression), expression] as const] : [];
      }),
    ).values(),
  ]);
  const bidExpressions = SmartCreationReviewBidExpressionSchema.array().parse(
    JSON.parse(bidExpressionsJson),
  );
  const currentGroup = workspace.graph.adGroups.find((item) => item.ref.lineageKey === activeGroup);
  const currentCampaign = workspace.graph.campaigns.find(
    (item) => item.ref.lineageKey === currentGroup?.campaignLineageKey,
  );
  // Cache only recommendation inputs: staged bid amounts and the active tab do not affect Amazon's estimate.
  const bidBatches: (typeof bidExpressions)[] = [];
  const sortedBidExpressions = [...bidExpressions].sort((a, b) =>
    bidExpressionKey(a).localeCompare(bidExpressionKey(b)),
  );
  for (let offset = 0; offset < sortedBidExpressions.length; offset += 100)
    bidBatches.push(sortedBidExpressions.slice(offset, offset + 100));
  const bidPreviewQueries = useQueries({
    queries: bidBatches.map((expressions) => ({
      queryKey: [
        "smart-creation-review-bids",
        planShopId,
        planId,
        activeGroup,
        workspace.summary.currencyCode,
        [...new Set(currentGroup?.products.map((product) => product.asin) ?? [])].sort(),
        expressions,
        currentCampaign?.biddingStrategy.effective,
        currentCampaign?.placements
          .map((placement) => ({
            placement: placement.placement,
            percentage: placement.percentage.effective,
          }))
          .sort((a, b) => a.placement.localeCompare(b.placement)),
      ],
      queryFn: () => {
        if (!planId || !planShopId) throw new Error("Bid preview requires a plan and shop");
        return fetchSmartCreationReviewBidPreview(planId, planShopId, {
          adGroupLineageKey: activeGroup,
          expressions,
        });
      },
      enabled:
        reviewStatus === "ready" &&
        !stale &&
        !negativeMode &&
        Boolean(planId && planShopId && bidExpressions.length && currentCampaign),
      staleTime: 5 * 60_000,
      gcTime: 10 * 60_000,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
  });
  const bidPreviewLoading = bidPreviewQueries.some((query) => query.isFetching);
  const bidPreviewFailed = bidPreviewQueries.some((query) => query.isError);
  const bidByExpression = new Map(
    bidPreviewQueries.flatMap(
      (query) =>
        query.data?.items.map((item) => [bidExpressionKey(item.expression), item] as const) ?? [],
    ),
  );
  const liveBidExpressionKeys = new Set(bidByExpression.keys());

  // Initial automatic prices remain auditable even if a later live estimate fails.
  for (const item of automaticInitialization?.items ?? [])
    if (
      !bidByExpression.has(
        bidExpressionKey({ type: "automatic", expressionType: item.expressionType }),
      )
    )
      bidByExpression.set(
        bidExpressionKey({ type: "automatic", expressionType: item.expressionType }),
        {
          expression: { type: "automatic", expressionType: item.expressionType },
          suggestedBid: item.bidValues[1],
          range: { min: item.bidValues[0], max: item.bidValues[2] },
        },
      );

  return {
    bidPreviewQueries,
    bidPreviewLoading,
    bidPreviewFailed,
    bidByExpression,
    liveBidExpressionKeys,
  };
}

export type ReviewBidPreview = ReturnType<typeof useReviewBidPreview>;
